import React, { useRef, useCallback, useEffect, useState, useMemo } from 'react';
import { useSceneStore } from '../../store/sceneStore';
import { useSceneViewStore } from '../../store/sceneViewStore';
import { useProjectStore } from '../../store/projectStore';
import { DEFAULT_SCENE_VIEW_SETTINGS } from '../../../core/types/scene.types';
import { GameObject } from '../../../core/types/gameobject.types';
import { SceneGrid } from './SceneGrid';
import { SelectionBox } from './SelectionBox';
import { TransformGizmo } from './TransformGizmo';
import { cn } from '../../utils/cn';
import { SpineObjectRenderer } from '../spine/SpineObjectRenderer';
import { useAssetUrl } from '../../hooks/useAssetUrl';
import { GraphicsSvgRenderer, GraphicsRendererData } from '../common/GraphicsSvgRenderer';
import { useProjectPanelStore } from '../../store/projectPanelStore';
import { loadImageDimensions } from '../inspector/PropertyEditors/TexturePropertyEditor';

interface SceneCanvasProps {
  width: number;
  height: number;
}

/** Walk up parent chain to compute absolute world position */
function getWorldPosition(
  gameObject: GameObject,
  gameObjects: Record<string, GameObject>
): { x: number; y: number } {
  let x = gameObject.transform.position.x;
  let y = gameObject.transform.position.y;
  let current = gameObject;
  while (current.parentId) {
    const parent = gameObjects[current.parentId];
    if (!parent) break;
    x += parent.transform.position.x;
    y += parent.transform.position.y;
    current = parent;
  }
  return { x, y };
}

/** Renders a sprite texture image, uses useAssetUrl hook */
const SpriteTextureImage: React.FC<{ texturePath: string | null; tint?: string; alpha?: number }> = ({
  texturePath,
  tint,
  alpha,
}) => {
  const { url } = useAssetUrl(texturePath);
  if (!url) return null;
  const hasTint = tint && tint !== '#ffffff';
  return (
    <div className="absolute inset-0 pointer-events-none" style={{ opacity: alpha ?? 1 }}>
      <img
        src={url}
        alt=""
        className="w-full h-full object-fill"
        draggable={false}
      />
      {hasTint && (
        <div
          className="absolute inset-0"
          style={{ backgroundColor: tint, mixBlendMode: 'multiply' }}
        />
      )}
    </div>
  );
};

interface GameObjectRendererProps {
  gameObject: GameObject;
  allGameObjects: Record<string, GameObject>;
  canvasWidth: number;
  canvasHeight: number;
  gameBoundsWidth: number;
  gameBoundsHeight: number;
  isSelected: boolean;
  isHovered: boolean;
  isDragOver: boolean;
  onClick: (e: React.MouseEvent) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
}

const GameObjectRenderer: React.FC<GameObjectRendererProps> = ({
  gameObject,
  allGameObjects,
  canvasWidth,
  canvasHeight,
  gameBoundsWidth,
  gameBoundsHeight,
  isSelected,
  isHovered,
  isDragOver,
  onClick,
  onMouseEnter,
  onMouseLeave,
  onDragOver,
  onDragLeave,
  onDrop,
}) => {
  const { viewport } = useSceneViewStore();
  const { isEffectivelyLocked, isEffectivelyVisible } = useSceneStore();
  const { zoom, panX, panY } = viewport;

  // Check if this object or any parent is hidden
  if (!isEffectivelyVisible(gameObject.id)) {
    return null;
  }

  const effectivelyLocked = isEffectivelyLocked(gameObject.id);

  const { transform } = gameObject;

  // Use world position (absolute) so children follow parent transforms
  const worldPos = gameObject.parentId
    ? getWorldPosition(gameObject, allGameObjects)
    : transform.position;

  // Calculate screen position (canvas center + object position + pan) * zoom
  const screenX = (canvasWidth / 2) + (worldPos.x + panX) * zoom;
  const screenY = (canvasHeight / 2) + (worldPos.y + panY) * zoom;

  // Object size based on type and component data
  let baseSizeW = 40;
  let baseSizeH = 40;

  // For sprites, use texture dimensions if available
  if (gameObject.type === 'sprite') {
    const spriteComp = gameObject.components.find((c) => c.type === 'sprite-renderer');
    const tw = spriteComp?.data?.textureWidth as number | undefined;
    const th = spriteComp?.data?.textureHeight as number | undefined;
    if (tw && th && tw > 0 && th > 0) {
      baseSizeW = tw;
      baseSizeH = th;
    } else {
      baseSizeW = 80;
      baseSizeH = 80;
    }
  }

  // For spine objects
  if (gameObject.type === 'spine') {
    baseSizeW = 100;
    baseSizeH = 100;
  }

  // For text objects, derive size from font size and text content
  if (gameObject.type === 'text') {
    const textComp = gameObject.components.find((c) => c.type === 'text-renderer');
    const fontSize = (textComp?.data?.fontSize as number) ?? 24;
    const textContent = (textComp?.data?.text as string) ?? 'New Text';
    const wordWrap = (textComp?.data?.wordWrap as boolean) ?? false;
    const wordWrapWidth = (textComp?.data?.wordWrapWidth as number) ?? 200;
    if (wordWrap) {
      baseSizeW = wordWrapWidth;
      const estimatedLines = Math.max(1, Math.ceil((textContent.length * fontSize * 0.6) / wordWrapWidth));
      baseSizeH = Math.max(fontSize * 1.4, estimatedLines * fontSize * 1.4);
    } else {
      baseSizeW = Math.max(40, textContent.length * fontSize * 0.6);
      baseSizeH = fontSize * 1.4;
    }
  }

  // For graphics objects, use the component's width/height/radius
  if (gameObject.type === 'graphics') {
    const gfxComp = gameObject.components.find((c) => c.type === 'graphics-renderer');
    if (gfxComp?.data) {
      const gShape = (gfxComp.data.shape as string) ?? 'rectangle';
      if (gShape === 'circle') {
        const r = (gfxComp.data.radius as number) ?? 50;
        baseSizeW = r * 2;
        baseSizeH = r * 2;
      } else {
        baseSizeW = (gfxComp.data.width as number) ?? 100;
        baseSizeH = (gfxComp.data.height as number) ?? 100;
      }
    }
  }

  // For containers (and slot game roots): read size from cascade-slot-part or sprite-renderer data
  if (gameObject.type === 'container' || gameObject.type === 'empty' || gameObject.type === 'cascade-slot' || gameObject.type === 'backend-slot-game') {
    // Check cascade-slot-part component for customWidth/customHeight
    const partComp = gameObject.components.find((c) => c.type === 'cascade-slot-part');
    const cw = partComp?.data?.customWidth as number | undefined;
    const ch = partComp?.data?.customHeight as number | undefined;
    if (cw && cw > 0) baseSizeW = cw;
    if (ch && ch > 0) baseSizeH = ch;
    // Also check sprite-renderer for textureWidth/textureHeight (containers that hold sprites)
    if (baseSizeW <= 40 || baseSizeH <= 40) {
      const spriteComp = gameObject.components.find((c) => c.type === 'sprite-renderer');
      const tw = spriteComp?.data?.textureWidth as number | undefined;
      const th = spriteComp?.data?.textureHeight as number | undefined;
      if (tw && tw > 0) baseSizeW = tw;
      if (th && th > 0) baseSizeH = th;
    }
    // Slot game roots: default to full game resolution
    if (gameObject.type === 'cascade-slot' || gameObject.type === 'backend-slot-game') {
      if (baseSizeW <= 40) baseSizeW = 720;
      if (baseSizeH <= 40) baseSizeH = 1280;
    }
    // Default container size
    if (baseSizeW <= 40) baseSizeW = 100;
    if (baseSizeH <= 40) baseSizeH = 60;
  }

  const width = baseSizeW * transform.scale.x * zoom;
  const height = baseSizeH * transform.scale.y * zoom;
  const zIndex = gameObject.transform.sortOrder ?? 0;

  // ---- Type-specific renderers ----

  // Spine: Use dedicated SpineObjectRenderer
  if (gameObject.type === 'spine') {
    return (
      <div
        className="absolute select-none"
        style={{
          left: screenX - width / 2,
          top: screenY - height / 2,
          width,
          height,
          opacity: effectivelyLocked ? 0.5 : 1,
          pointerEvents: effectivelyLocked ? 'none' : 'auto',
          outline: isDragOver ? '2px dashed rgba(59, 130, 246, 0.8)' : undefined,
          outlineOffset: isDragOver ? '2px' : undefined,
        }}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        title={`${gameObject.name} (${gameObject.type})`}
      >
        <SpineObjectRenderer
          gameObject={gameObject}
          screenX={width / 2}
          screenY={height / 2}
          width={width}
          height={height}
          zoom={zoom}
          isSelected={isSelected}
          isHovered={isHovered}
        />
        {(isSelected || isHovered) && (
          <div
            className="absolute -bottom-5 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs bg-zinc-800 px-1.5 py-0.5 rounded text-zinc-300 z-20"
            style={{ transform: `translateX(-50%) rotate(-${transform.rotation}deg)` }}
          >
            {gameObject.name}
          </div>
        )}
      </div>
    );
  }

  // ---- Sprite with texture: render the actual image ----
  const spriteComp = gameObject.type === 'sprite'
    ? gameObject.components.find((c) => c.type === 'sprite-renderer')
    : null;
  const texturePath = (spriteComp?.data?.texturePath as string | null) ?? null;

  if (gameObject.type === 'sprite' && texturePath) {
    const anchorX = transform.anchor?.x ?? 0.5;
    const anchorY = transform.anchor?.y ?? 0.5;
    const flipX = (spriteComp?.data?.flipX as boolean) ?? false;
    const flipY = (spriteComp?.data?.flipY as boolean) ?? false;
    const flipScaleX = flipX ? ' scaleX(-1)' : '';
    const flipScaleY = flipY ? ' scaleY(-1)' : '';
    return (
      <div
        className="absolute cursor-pointer select-none"
        style={{
          left: screenX - width * anchorX,
          top: screenY - height * anchorY,
          width,
          height,
          zIndex,
          transform: `rotate(${transform.rotation}deg)${flipScaleX}${flipScaleY}`,
          transformOrigin: `${anchorX * 100}% ${anchorY * 100}%`,
          opacity: effectivelyLocked ? 0.5 : 1,
          pointerEvents: effectivelyLocked ? 'none' : 'auto',
          border: isSelected
            ? '2px solid rgba(59, 130, 246, 1)'
            : isHovered
              ? '1px solid rgba(96, 165, 250, 0.5)'
              : '1px solid transparent',
          boxSizing: 'border-box',
          outline: isDragOver ? '2px dashed rgba(59, 130, 246, 0.8)' : undefined,
          outlineOffset: isDragOver ? '2px' : undefined,
        }}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        title={`${gameObject.name} (${gameObject.type})`}
      >
        <SpriteTextureImage
          texturePath={texturePath}
          tint={spriteComp?.data?.tint as string | undefined}
          alpha={spriteComp?.data?.alpha as number | undefined}
        />
        {(isSelected || isHovered) && (
          <div
            className="absolute -bottom-5 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs bg-zinc-800 px-1.5 py-0.5 rounded text-zinc-300 z-20"
            style={{ transform: `translateX(-50%) rotate(-${transform.rotation}deg)` }}
          >
            {gameObject.name}
          </div>
        )}
      </div>
    );
  }

  // ---- Text renderer: render actual text with CSS styles ----
  if (gameObject.type === 'text') {
    const textComp = gameObject.components.find((c) => c.type === 'text-renderer');
    const textContent = (textComp?.data?.text as string) ?? 'New Text';
    const fontFamily = (textComp?.data?.fontFamily as string) ?? 'Arial';
    const fontSize = (textComp?.data?.fontSize as number) ?? 24;
    const fontWeight = (textComp?.data?.fontWeight as string) ?? 'normal';
    const fontStyle = (textComp?.data?.fontStyle as string) ?? 'normal';
    const fill = (textComp?.data?.fill as string) ?? '#ffffff';
    const align = (textComp?.data?.align as string) ?? 'left';
    const wordWrap = (textComp?.data?.wordWrap as boolean) ?? false;
    const letterSpacing = (textComp?.data?.letterSpacing as number) ?? 0;
    const lineHeight = (textComp?.data?.lineHeight as number) || undefined;

    return (
      <div
        className="absolute cursor-pointer select-none"
        style={{
          left: screenX - width / 2,
          top: screenY - height / 2,
          width,
          height,
          zIndex,
          transform: `rotate(${transform.rotation}deg)`,
          transformOrigin: 'center',
          opacity: effectivelyLocked ? 0.5 : 1,
          pointerEvents: effectivelyLocked ? 'none' : 'auto',
          border: isSelected
            ? '2px solid rgba(59, 130, 246, 1)'
            : isHovered
              ? '1px solid rgba(96, 165, 250, 0.5)'
              : '1px solid transparent',
          boxSizing: 'border-box',
          outline: isDragOver ? '2px dashed rgba(59, 130, 246, 0.8)' : undefined,
          outlineOffset: isDragOver ? '2px' : undefined,
        }}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        title={`${gameObject.name} (${gameObject.type})`}
      >
        <div
          className="absolute inset-0 overflow-hidden pointer-events-none"
          style={{
            fontFamily,
            fontSize: fontSize * zoom,
            fontWeight,
            fontStyle,
            color: fill,
            textAlign: align as React.CSSProperties['textAlign'],
            whiteSpace: wordWrap ? 'pre-wrap' : 'nowrap',
            letterSpacing: letterSpacing * zoom,
            lineHeight: lineHeight ? `${lineHeight * zoom}px` : undefined,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent:
              align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start',
          }}
        >
          {textContent}
        </div>
        {(isSelected || isHovered) && (
          <div
            className="absolute -bottom-5 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs bg-zinc-800 px-1.5 py-0.5 rounded text-zinc-300 z-20"
            style={{ transform: `translateX(-50%) rotate(-${transform.rotation}deg)` }}
          >
            {gameObject.name}
          </div>
        )}
      </div>
    );
  }

  // ---- Graphics renderer: render shape via SVG ----
  if (gameObject.type === 'graphics') {
    const gfxComp = gameObject.components.find((c) => c.type === 'graphics-renderer');
    const gfxData = (gfxComp?.data ?? {}) as GraphicsRendererData;

    return (
      <div
        className="absolute cursor-pointer select-none"
        style={{
          left: screenX - width / 2,
          top: screenY - height / 2,
          width,
          height,
          zIndex,
          transform: `rotate(${transform.rotation}deg)`,
          transformOrigin: 'center',
          opacity: effectivelyLocked ? 0.5 : 1,
          pointerEvents: effectivelyLocked ? 'none' : 'auto',
          border: isSelected
            ? '2px solid rgba(59, 130, 246, 1)'
            : isHovered
              ? '1px solid rgba(96, 165, 250, 0.5)'
              : '1px solid transparent',
          boxSizing: 'border-box',
          outline: isDragOver ? '2px dashed rgba(59, 130, 246, 0.8)' : undefined,
          outlineOffset: isDragOver ? '2px' : undefined,
        }}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        title={`${gameObject.name} (${gameObject.type})`}
      >
        <GraphicsSvgRenderer
          data={gfxData}
          renderWidth={width}
          renderHeight={height}
        />
        {(isSelected || isHovered) && (
          <div
            className="absolute -bottom-5 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs bg-zinc-800 px-1.5 py-0.5 rounded text-zinc-300 z-20"
            style={{ transform: `translateX(-50%) rotate(-${transform.rotation}deg)` }}
          >
            {gameObject.name}
          </div>
        )}
      </div>
    );
  }

  // ---- Default renderer: wireframe style (solid gray, no transparency) ----
  // Slot game roots render invisible
  if (gameObject.type === 'backend-slot-game' || gameObject.type === 'cascade-slot') {
    return (
      <div
        className="absolute cursor-pointer select-none"
        style={{
          left: screenX - width / 2,
          top: screenY - height / 2,
          width,
          height,
          zIndex,
          pointerEvents: effectivelyLocked ? 'none' : 'auto',
          border: isSelected ? '2px solid rgba(59, 130, 246, 1)' : '1px solid transparent',
          boxSizing: 'border-box',
        }}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        title={`${gameObject.name} (${gameObject.type})`}
      />
    );
  }

  // ---- Default renderer: wireframe outline + text only, no fill ----
  const isLeaf = gameObject.childIds.length === 0;
  const borderColor = isLeaf ? '#71717a' : '#52525b'; // zinc-500 for leaves, zinc-600 for containers

  return (
    <div
      className="absolute cursor-pointer select-none"
      style={{
        left: screenX - width / 2,
        top: screenY - height / 2,
        width,
        height,
        zIndex,
        transform: `rotate(${transform.rotation}deg)`,
        transformOrigin: 'center',
        opacity: effectivelyLocked ? 0.5 : 1,
        pointerEvents: effectivelyLocked ? 'none' : 'auto',
        border: isSelected
          ? '2px solid #3b82f6'
          : isHovered
            ? '2px solid #60a5fa'
            : `1px solid ${borderColor}`,
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        outline: isDragOver ? '2px dashed #3b82f6' : undefined,
        outlineOffset: isDragOver ? '2px' : undefined,
      }}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      title={`${gameObject.name} (${gameObject.type})`}
    >
      {/* Name label on leaf nodes, or when selected/hovered */}
      {(isLeaf || isSelected || isHovered) && (
        <span
          className="text-zinc-400 text-center pointer-events-none select-none"
          style={{
            fontSize: Math.max(7, Math.min(10, width / 12)),
            lineHeight: 1.2,
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            padding: '0 2px',
          }}
        >
          {gameObject.name}
        </span>
      )}
    </div>
  );
};

const FocusBreadcrumb: React.FC = () => {
  const focusedContainerId = useSceneStore((s) => s.focusedContainerId);
  const gameObjects = useSceneStore((s) => s.gameObjects);
  const rootIds = useSceneStore((s) => s.rootIds);

  if (!focusedContainerId) return null;

  // Build path from root to focused container
  const path: { id: string; name: string }[] = [];
  let current = gameObjects[focusedContainerId];
  while (current) {
    path.unshift({ id: current.id, name: current.name });
    if (!current.parentId || rootIds.includes(current.id)) break;
    current = gameObjects[current.parentId];
  }

  return (
    <div className="absolute top-1 left-1 z-10 flex items-center gap-1 px-2 py-1 bg-zinc-800/90 rounded text-xs text-zinc-400 backdrop-blur-sm">
      <button
        className="hover:text-zinc-200 transition-colors"
        onClick={() => useSceneStore.getState().setFocusedContainer(null)}
      >
        Scene
      </button>
      {path.map((item, i) => (
        <React.Fragment key={item.id}>
          <span className="text-zinc-600">/</span>
          <button
            className={`hover:text-zinc-200 transition-colors ${i === path.length - 1 ? 'text-blue-400' : ''}`}
            onClick={() => {
              if (i < path.length - 1) {
                useSceneStore.getState().setFocusedContainer(item.id);
              }
            }}
          >
            {item.name}
          </button>
        </React.Fragment>
      ))}
    </div>
  );
};

export const SceneCanvas: React.FC<SceneCanvasProps> = ({ width, height }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isPanning, setIsPanning] = useState(false);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const panStartRef = useRef<{ x: number; y: number; panX: number; panY: number } | null>(
    null
  );

  const {
    gameObjects,
    selectedIds,
    hoveredId,
    select,
    selectMultiple,
    clearSelection,
    setHovered,
    isEffectivelyLocked,
    isEffectivelyVisible,
  } = useSceneStore();

  const {
    viewport,
    activeTool,
    setZoom,
    setPan,
    setMousePosition,
    boxSelection,
    startBoxSelection,
    updateBoxSelection,
    endBoxSelection,
  } = useSceneViewStore();

  const { zoom, panX, panY } = viewport;

  // Get project resolution for canvas bounds (fallback to defaults)
  const currentProject = useProjectStore((s) => s.currentProject);
  const canvasBoundsWidth = currentProject?.resolution?.width ?? DEFAULT_SCENE_VIEW_SETTINGS.canvasWidth;
  const canvasBoundsHeight = currentProject?.resolution?.height ?? DEFAULT_SCENE_VIEW_SETTINGS.canvasHeight;

  // Convert screen coordinates to world coordinates
  const screenToWorld = useCallback(
    (screenX: number, screenY: number) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return { x: 0, y: 0 };

      const canvasCenterX = width / 2;
      const canvasCenterY = height / 2;

      const worldX = (screenX - rect.left - canvasCenterX) / zoom - panX;
      const worldY = (screenY - rect.top - canvasCenterY) / zoom - panY;

      return { x: worldX, y: worldY };
    },
    [width, height, zoom, panX, panY]
  );

  // Handle mouse move for tracking position
  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      const worldPos = screenToWorld(e.clientX, e.clientY);
      setMousePosition(worldPos.x, worldPos.y);

      // Update box selection if active
      if (boxSelection?.isSelecting) {
        const rect = containerRef.current?.getBoundingClientRect();
        if (rect) {
          updateBoxSelection(e.clientX - rect.left, e.clientY - rect.top);
        }
      }

      // Handle panning
      if (isPanning && panStartRef.current) {
        const deltaX = (e.clientX - panStartRef.current.x) / zoom;
        const deltaY = (e.clientY - panStartRef.current.y) / zoom;
        setPan(panStartRef.current.panX + deltaX, panStartRef.current.panY + deltaY);
      }
    },
    [
      screenToWorld,
      setMousePosition,
      boxSelection,
      updateBoxSelection,
      isPanning,
      zoom,
      setPan,
    ]
  );

  // Handle mouse down
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      // Middle mouse button for panning
      if (e.button === 1) {
        e.preventDefault();
        setIsPanning(true);
        panStartRef.current = {
          x: e.clientX,
          y: e.clientY,
          panX,
          panY,
        };
        document.body.style.cursor = 'grabbing';
        return;
      }

      // Left mouse button
      if (e.button === 0) {
        // Pan tool: start panning
        if (activeTool === 'pan') {
          setIsPanning(true);
          panStartRef.current = {
            x: e.clientX,
            y: e.clientY,
            panX,
            panY,
          };
          document.body.style.cursor = 'grabbing';
          return;
        }

        // Select tool: start box selection or clear selection
        if (activeTool === 'select' || activeTool === 'move' || activeTool === 'scale' || activeTool === 'rotate') {
          // Check if clicking on empty space
          const target = e.target as HTMLElement;
          const isCanvasClick =
            target === containerRef.current ||
            target.classList.contains('scene-canvas-bg');

          if (isCanvasClick) {
            // Start box selection
            const rect = containerRef.current?.getBoundingClientRect();
            if (rect) {
              startBoxSelection(e.clientX - rect.left, e.clientY - rect.top);
            }

            // Clear selection if not holding shift
            if (!e.shiftKey) {
              clearSelection();
            }
          }
        }
      }
    },
    [activeTool, panX, panY, clearSelection, startBoxSelection]
  );

  // Handle mouse up
  const handleMouseUp = useCallback(
    (e: React.MouseEvent) => {
      // End panning
      if (isPanning) {
        setIsPanning(false);
        panStartRef.current = null;
        document.body.style.cursor = '';
      }

      // End box selection
      if (boxSelection?.isSelecting) {
        // Find objects within selection box
        const rect = containerRef.current?.getBoundingClientRect();
        if (rect) {
          const { startX, startY, endX, endY } = boxSelection;
          const boxLeft = Math.min(startX, endX);
          const boxRight = Math.max(startX, endX);
          const boxTop = Math.min(startY, endY);
          const boxBottom = Math.max(startY, endY);

          // Check which objects are within the box
          const objectsInBox: string[] = [];

          Object.values(gameObjects).forEach((obj) => {
            if (!isEffectivelyVisible(obj.id) || isEffectivelyLocked(obj.id)) return;

            // Use world position for accurate box selection
            const worldPos = getWorldPosition(obj, gameObjects);
            const screenX =
              width / 2 + (worldPos.x + panX) * zoom;
            const screenY =
              height / 2 + (worldPos.y + panY) * zoom;

            if (
              screenX >= boxLeft &&
              screenX <= boxRight &&
              screenY >= boxTop &&
              screenY <= boxBottom
            ) {
              objectsInBox.push(obj.id);
            }
          });

          if (objectsInBox.length > 0) {
            if (e.shiftKey) {
              // Add to selection
              selectMultiple([...new Set([...selectedIds, ...objectsInBox])]);
            } else {
              selectMultiple(objectsInBox);
            }
          }
        }

        endBoxSelection();
      }
    },
    [
      isPanning,
      boxSelection,
      endBoxSelection,
      gameObjects,
      width,
      height,
      panX,
      panY,
      zoom,
      selectedIds,
      selectMultiple,
      isEffectivelyLocked,
      isEffectivelyVisible,
    ]
  );

  // Handle mouse wheel for zooming
  // Note: Using useEffect to add non-passive wheel listener to allow preventDefault
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();

      const zoomDelta = e.deltaY > 0 ? -0.1 : 0.1;
      const newZoom = Math.max(0.1, Math.min(5, zoom + zoomDelta * zoom));
      setZoom(newZoom);
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, [zoom, setZoom]);

  // Handle object click — drill-down container focus logic
  const handleObjectClick = useCallback(
    (objectId: string, e: React.MouseEvent) => {
      e.stopPropagation();
      const { selectedIds, focusedContainerId, gameObjects: gos, rootIds: rIds } = useSceneStore.getState();

      // Shift/Ctrl → existing multi-select behavior
      if (e.shiftKey || e.ctrlKey || e.metaKey) {
        useSceneStore.getState().select(objectId, true);
        return;
      }

      // Helper: find root ancestor (direct child of root level)
      const getRootAncestor = (id: string): string => {
        let current = gos[id];
        while (current && current.parentId && !rIds.includes(current.id)) {
          current = gos[current.parentId];
        }
        return current?.id ?? id;
      };

      // Helper: find the direct child of a container in the ancestor chain of targetId
      const getDirectChildInChain = (containerId: string, targetId: string): string | null => {
        let current = gos[targetId];
        while (current) {
          if (current.parentId === containerId) return current.id;
          if (!current.parentId) return null;
          current = gos[current.parentId];
        }
        return null;
      };

      // Helper: check if targetId is a descendant of ancestorId
      const isDescendantOf = (targetId: string, ancestorId: string): boolean => {
        let current = gos[targetId];
        while (current) {
          if (current.parentId === ancestorId) return true;
          if (!current.parentId) return false;
          current = gos[current.parentId];
        }
        return false;
      };

      if (!focusedContainerId) {
        // No focused container — work at root level
        const rootAncestor = getRootAncestor(objectId);
        if (selectedIds.length === 1 && selectedIds[0] === rootAncestor) {
          // Root already selected → drill in
          const children = gos[rootAncestor]?.childIds ?? [];
          if (children.length > 0) {
            useSceneStore.getState().enterContainer(rootAncestor);
            const directChild = getDirectChildInChain(rootAncestor, objectId);
            if (directChild) {
              useSceneStore.getState().select(directChild, false);
            }
          }
        } else {
          // Select the root ancestor
          useSceneStore.getState().select(rootAncestor, false);
        }
      } else {
        // Inside a focused container
        if (!isDescendantOf(objectId, focusedContainerId) && objectId !== focusedContainerId) {
          // Clicked outside the focused container tree → reset
          useSceneStore.getState().setFocusedContainer(null);
          const rootAncestor = getRootAncestor(objectId);
          useSceneStore.getState().select(rootAncestor, false);
          return;
        }

        const directChild = getDirectChildInChain(focusedContainerId, objectId);
        if (!directChild) {
          // Clicked on the focused container itself
          return;
        }

        if (selectedIds.length === 1 && selectedIds[0] === directChild) {
          // Already selected → drill deeper if it has children
          const childObj = gos[directChild];
          if (childObj && (childObj.childIds?.length ?? 0) > 0) {
            useSceneStore.getState().enterContainer(directChild);
            const nextChild = getDirectChildInChain(directChild, objectId);
            if (nextChild) {
              useSceneStore.getState().select(nextChild, false);
            }
          }
        } else {
          // Select this direct child
          useSceneStore.getState().select(directChild, false);
        }
      }
    },
    []
  );

  // Handle asset drop from Project Panel onto a game object
  const handleAssetDrop = useCallback((e: React.DragEvent, targetObject: GameObject) => {
    const assetId = e.dataTransfer.getData('text/plain');
    if (!assetId) return;

    const { assets } = useProjectPanelStore.getState();
    const asset = assets[assetId];
    if (!asset) return;

    const { updateComponent, gameObjects: gos } = useSceneStore.getState();
    const target = gos[targetObject.id];
    if (!target) return;

    if (asset.type === 'sprite') {
      // Find existing sprite-renderer component
      const spriteComp = target.components.find(c => c.type === 'sprite-renderer');
      if (spriteComp) {
        // Update existing sprite-renderer with new texture path
        updateComponent(target.id, spriteComp.id, {
          texturePath: asset.path,
        });

        // Load image dimensions — only set if no existing size is defined
        // (e.g., hierarchy-created symbols already have 140x140, keep that)
        const existingW = spriteComp.data?.textureWidth as number | undefined;
        const existingH = spriteComp.data?.textureHeight as number | undefined;
        if (!existingW || !existingH || existingW <= 0 || existingH <= 0) {
          loadImageDimensions(asset.path)
            .then(({ width: imgW, height: imgH }) => {
              updateComponent(target.id, spriteComp.id, {
                texturePath: asset.path,
                textureWidth: imgW,
                textureHeight: imgH,
              });
            })
            .catch(() => {
              // Texture path already set above; dimensions just won't update
            });
        }
      } else {
        console.warn(`[SceneCanvas] Target "${target.name}" has no sprite-renderer component`);
      }
    } else if (asset.type === 'spine') {
      // Find existing spine-renderer component
      const spineComp = target.components.find(c => c.type === 'spine-renderer');
      if (spineComp) {
        updateComponent(target.id, spineComp.id, {
          spineAssetPath: asset.path,
        });
      } else {
        console.warn(`[SceneCanvas] Target "${target.name}" has no spine-renderer component`);
      }
    }
  }, []);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't capture if input is focused or user is typing in an editor
      const activeElement = document.activeElement;
      if (
        activeElement?.tagName === 'INPUT' ||
        activeElement?.tagName === 'TEXTAREA' ||
        activeElement?.getAttribute('contenteditable') === 'true' ||
        activeElement?.closest('.monaco-editor') ||
        activeElement?.closest('[data-script-editor]')
      ) {
        return;
      }

      switch (e.key.toLowerCase()) {
        case 'home':
          // Reset view
          setPan(0, 0);
          setZoom(1);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [setPan, setZoom]);

  // Prevent context menu
  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
  }, []);

  // Build hierarchy order index (depth-first traversal of the scene tree).
  // Objects later in childIds[] (lower in the Hierarchy panel) get a higher index
  // and render on top when sortOrder is the same.
  const hierarchyOrder = useMemo(() => {
    const order = new Map<string, number>();
    let index = 0;
    const walk = (id: string) => {
      const obj = gameObjects[id];
      if (!obj) return;
      order.set(id, index++);
      for (const childId of obj.childIds) {
        walk(childId);
      }
    };
    // Walk root objects (those with no parent)
    for (const obj of Object.values(gameObjects)) {
      if (!obj.parentId) walk(obj.id);
    }
    return order;
  }, [gameObjects]);

  // Get sorted game objects for rendering (back to front)
  // Primary sort: sortOrder (lower = behind, higher = on top)
  // Secondary sort: hierarchy order (later in tree = on top, matches Hierarchy panel)
  const sortedObjects = Object.values(gameObjects).sort((a, b) => {
    const orderDiff = (a.transform.sortOrder ?? 0) - (b.transform.sortOrder ?? 0);
    if (orderDiff !== 0) return orderDiff;
    return (hierarchyOrder.get(a.id) ?? 0) - (hierarchyOrder.get(b.id) ?? 0);
  });

  // Get selected objects for gizmo rendering
  const selectedObjects = selectedIds
    .map((id) => gameObjects[id])
    .filter(Boolean);

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative w-full h-full overflow-hidden bg-zinc-950',
        activeTool === 'pan' && 'cursor-grab',
        isPanning && 'cursor-grabbing'
      )}
      onMouseMove={handleMouseMove}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onContextMenu={handleContextMenu}
    >
      {/* Focus breadcrumb */}
      <FocusBreadcrumb />

      {/* Background layer */}
      <div
        className="scene-canvas-bg absolute inset-0 bg-zinc-950"
        style={{
          backgroundImage:
            'radial-gradient(circle at center, rgba(255,255,255,0.03) 0%, transparent 70%)',
        }}
      />

      {/* Grid layer */}
      <SceneGrid width={width} height={height} />

      {/* Canvas bounds indicator */}
      <div
        className="absolute pointer-events-none border border-zinc-700 border-dashed"
        style={{
          left: width / 2 + panX * zoom - (canvasBoundsWidth * zoom) / 2,
          top: height / 2 + panY * zoom - (canvasBoundsHeight * zoom) / 2,
          width: canvasBoundsWidth * zoom,
          height: canvasBoundsHeight * zoom,
        }}
      />

      {/* Game Objects layer */}
      {sortedObjects.map((obj) => (
        <GameObjectRenderer
          key={obj.id}
          gameObject={obj}
          allGameObjects={gameObjects}
          canvasWidth={width}
          canvasHeight={height}
          gameBoundsWidth={canvasBoundsWidth}
          gameBoundsHeight={canvasBoundsHeight}
          isSelected={selectedIds.includes(obj.id)}
          isHovered={hoveredId === obj.id}
          isDragOver={dragOverId === obj.id}
          onClick={(e) => handleObjectClick(obj.id, e)}
          onMouseEnter={() => setHovered(obj.id)}
          onMouseLeave={() => setHovered(null)}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setDragOverId(obj.id);
          }}
          onDragLeave={(e) => {
            e.stopPropagation();
            setDragOverId(null);
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setDragOverId(null);
            handleAssetDrop(e, obj);
          }}
        />
      ))}

      {/* Transform Gizmos layer */}
      {selectedObjects.map((obj) => (
        <TransformGizmo
          key={`gizmo-${obj.id}`}
          gameObject={obj}
          canvasWidth={width}
          canvasHeight={height}
        />
      ))}

      {/* Selection Box layer */}
      <SelectionBox />

    </div>
  );
};
